import { useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { AvailableTransition } from '../model/types';

/**
 * The reason a `reasonRequired` transition asks for (SDD-013 §6.2): recorded on the version
 * row, refused by the service without it (409 TRANSITION_REASON_REQUIRED). The dialog asks
 * before sending rather than after — the refusal is the API's boundary, the question is the UI's.
 */
export function TransitionReasonDialog({
  transition,
  open,
  pending,
  onSubmit,
  onClose,
}: {
  readonly transition: AvailableTransition | null;
  readonly open: boolean;
  readonly pending: boolean;
  readonly onSubmit: (reason: string) => void;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const missing = reason.trim() === '';

  const close = (): void => {
    setReason('');
    setSubmitted(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>{t('leads.transition.reasonTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('leads.transition.reasonDescription')}
          </Typography>
          <TextField
            label={t('leads.transition.reason')}
            value={reason}
            required
            fullWidth
            multiline
            minRows={2}
            size="small"
            onChange={(event) => {
              setReason(event.target.value);
            }}
            error={submitted && missing}
            helperText={submitted && missing ? t('leads.transition.reasonRequired') : undefined}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('leads.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={pending}
          onClick={() => {
            setSubmitted(true);
            if (missing) return;
            onSubmit(reason.trim());
            setReason('');
            setSubmitted(false);
          }}
        >
          {transition?.name !== undefined && transition.name !== '' ? transition.name : t('leads.transition.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
